import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { T } from '../components/ui';

interface Props {
  onLoginClick: () => void;
}

export default function RegisterPage({ onLoginClick }: Props) {
  const { register } = useAuth();
  const [form, setForm] = useState({
    company_name: '', display_name: '', email: '', password: '', confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (form.password.length < 10) { setError('Password must be at least 10 characters.'); return; }
    setLoading(true);
    try {
      await register({
        company_name: form.company_name,
        display_name: form.display_name,
        email: form.email,
        password: form.password,
      });
      // Auth context will update user; App.tsx will redirect to onboarding
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registration failed.';
      setError(msg.includes('already') ? 'An account with this email already exists.' : msg);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px',
    background: T.card, border: `1px solid ${T.border}`,
    borderRadius: 8, color: T.text, fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, color: T.sub,
    marginBottom: 6, fontWeight: 500,
  };

  return (
    <div style={{
      minHeight: '100vh', background: T.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.text, letterSpacing: '-0.5px' }}>
            Trace<span style={{ color: T.copper }}>Full</span>
          </div>
          <div style={{ fontSize: 13, color: T.sub, marginTop: 6 }}>
            Supply chain intelligence platform
          </div>
        </div>

        <div style={{
          background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: '32px 36px',
        }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: T.text }}>
            Start your free trial
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 13, color: T.sub }}>
            14 days free · No credit card required
          </p>

          {error && (
            <div style={{
              background: `${T.crimson}15`, border: `1px solid ${T.crimson}40`,
              borderRadius: 8, padding: '10px 14px', marginBottom: 20,
              fontSize: 13, color: T.crimson,
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Company name</label>
              <input
                style={inputStyle}
                placeholder="Acme Foods Inc."
                value={form.company_name}
                onChange={set('company_name')}
                onKeyDown={e => e.key === 'Enter' && submit()}
              />
            </div>
            <div>
              <label style={labelStyle}>Your name</label>
              <input
                style={inputStyle}
                placeholder="Jane Smith"
                value={form.display_name}
                onChange={set('display_name')}
                onKeyDown={e => e.key === 'Enter' && submit()}
              />
            </div>
            <div>
              <label style={labelStyle}>Work email</label>
              <input
                style={inputStyle}
                type="email"
                placeholder="jane@acmefoods.com"
                value={form.email}
                onChange={set('email')}
                onKeyDown={e => e.key === 'Enter' && submit()}
              />
            </div>
            <div>
              <label style={labelStyle}>Password <span style={{ color: T.dim }}>(min. 10 characters)</span></label>
              <input
                style={inputStyle}
                type="password"
                placeholder="••••••••••••"
                value={form.password}
                onChange={set('password')}
                onKeyDown={e => e.key === 'Enter' && submit()}
              />
            </div>
            <div>
              <label style={labelStyle}>Confirm password</label>
              <input
                style={inputStyle}
                type="password"
                placeholder="••••••••••••"
                value={form.confirm}
                onChange={set('confirm')}
                onKeyDown={e => e.key === 'Enter' && submit()}
              />
            </div>
          </div>

          <button
            onClick={submit}
            disabled={loading || !form.company_name || !form.email || !form.password}
            style={{
              width: '100%', marginTop: 24,
              padding: '12px',
              background: loading ? T.border : T.copper,
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.15s',
            }}
          >
            {loading ? 'Creating account…' : 'Create account →'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: T.sub }}>
            Already have an account?{' '}
            <span
              onClick={onLoginClick}
              style={{ color: T.copper, cursor: 'pointer', fontWeight: 500 }}
            >
              Sign in
            </span>
          </div>
        </div>

        {/* Trust signals */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 28,
          marginTop: 28, flexWrap: 'wrap',
        }}>
          {['FDA FSMA 204 ready', 'EU DPP compliant', 'SOC 2 in progress'].map(t => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.dim }}>
              <span style={{ color: T.sage }}>✓</span> {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
